// src/pages/ResetPassword.jsx
import React, { useState, useEffect } from "react";
import { verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import { auth } from "../firebase"; // Adjust path to your firebase config

export default function ResetPassword() {
  const [oobCode, setOobCode] = useState(null);
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState("verifying"); // verifying, ready, success, error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    // Grab the special 'oobCode' parameter from the URL that Firebase sends

    auth.signOut(); 
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("oobCode");

    if (!code) {
      setStatus("error");
      setErrorMessage("The password reset link is invalid or missing.");
      return;
    }

    setOobCode(code);

    // Verify the code with Firebase to ensure it hasn't expired
    verifyPasswordResetCode(auth, code)
      .then((userEmail) => {
        setEmail(userEmail);
        setStatus("ready");
      })
      .catch((error) => {
        console.error(error);
        setStatus("error");
        setErrorMessage("This password reset link has expired or was already used. Please request a new one.");
      });
  }, []);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }
    if (newPassword.length < 6) {
      setErrorMessage("Password must be at least 6 characters long.");
      return;
    }

    setStatus("updating");

    try {
      // Send the new password to Firebase
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStatus("success");
    } catch (error) {
      console.error(error);
      setStatus("error");
      setErrorMessage(error.message || "Failed to reset password. Please try again.");
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6 relative overflow-hidden">
      {/* Subtle decorative background matching your Account page */}
      <div className="pointer-events-none absolute inset-0 opacity-40 dark:opacity-30">
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full blur-3xl bg-sky-300/40 dark:bg-sky-500/20" />
        <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full blur-3xl bg-indigo-300/40 dark:bg-indigo-500/20" />
      </div>

      <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-8 z-10">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black tracking-tight text-gray-900 dark:text-gray-100">
            Trapound
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2">Set a new password for your account</p>
        </div>

        {status === "verifying" && (
          <div className="text-center text-gray-600 dark:text-gray-300 font-semibold animate-pulse">
            Verifying secure link...
          </div>
        )}

        {(status === "ready" || status === "updating") && (
          <form onSubmit={handleResetPassword} className="space-y-5">
            <div className="text-sm text-center font-medium text-blue-600 dark:text-blue-400 mb-4 bg-blue-50 dark:bg-blue-900/30 py-2 rounded-lg">
              {email}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 outline-none text-gray-900 dark:text-white transition"
                placeholder="At least 6 characters"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 outline-none text-gray-900 dark:text-white transition"
                placeholder="Type it again"
                required
              />
            </div>

            {errorMessage && (
              <div className="p-3 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm font-semibold rounded-lg text-center border border-red-100 dark:border-red-800">
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={status === "updating" || !newPassword || !confirmPassword}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl disabled:opacity-50 transition shadow-md"
            >
              {status === "updating" ? "Updating..." : "Reset Password"}
            </button>
          </form>
        )}

        {status === "success" && (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/40 mb-4">
              <svg className="h-8 w-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Password Updated!</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">Your password has been successfully reset. You can now log in securely.</p>
            <a
              href="/login"
              className="inline-block w-full py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 font-bold rounded-xl transition shadow-md"
            >
              Go to Login
            </a>
          </div>
        )}

        {status === "error" && errorMessage !== "Passwords do not match." && errorMessage !== "Password must be at least 6 characters long." && (
          <div className="text-center">
            <div className="p-4 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm font-semibold rounded-xl border border-red-100 dark:border-red-800 mb-6">
              {errorMessage}
            </div>
            <a
              href="/login"
              className="inline-block w-full py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600 font-bold rounded-xl transition"
            >
              Return to Login
            </a>
          </div>
        )}
      </div>
    </main>
  );
}